import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ChevronLeft,
  BookOpen,
  CheckCircle,
  Circle,
  Bookmark,
  BookmarkCheck,
  Search,
  X,
  Clock,
  Award,
  TrendingUp,
  Menu,
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { userGuideCategories } from '../content/userGuideData';
import { UserGuideArticle } from '../types/userGuide.types';
import UserGuideSidebar from '../components/UserGuideSidebar';
import { userGuideService } from '../services/userGuideService';
import { toast } from '@shared/components/ui/Toaster';

const UserGuidePage: React.FC = () => {
  const { user } = useAuth();
  const [selectedArticle, setSelectedArticle] = useState<UserGuideArticle | null>(null);
  const [completedArticles, setCompletedArticles] = useState<Set<string>>(new Set());
  const [bookmarkedArticles, setBookmarkedArticles] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const articleStartTime = useRef<number>(Date.now());
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadUserProgress();
    }
  }, [user]);

  useEffect(() => {
    if (userGuideCategories.length > 0 && !selectedArticle) {
      setSelectedArticle(userGuideCategories[0].articles[0]);
    }
  }, []);

  useEffect(() => {
    if (selectedArticle) {
      articleStartTime.current = Date.now();
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }

    return () => {
      if (selectedArticle && user) {
        const timeSpent = Math.floor((Date.now() - articleStartTime.current) / 1000);
        if (timeSpent > 5) {
          userGuideService.recordArticleView(user.id, selectedArticle.id, timeSpent);
        }
      }
    };
  }, [selectedArticle, user]);

  const loadUserProgress = async () => {
    if (!user) return;

    const progressMap = await userGuideService.getUserProgress(user.id);
    const completed = new Set<string>();
    const bookmarked = new Set<string>();

    progressMap.forEach((progress, articleId) => {
      if (progress.completed) completed.add(articleId);
      if (progress.bookmarked) bookmarked.add(articleId);
    });

    setCompletedArticles(completed);
    setBookmarkedArticles(bookmarked);

    const totalArticles = userGuideCategories.reduce(
      (sum, cat) => sum + cat.articles.length,
      0
    );
    const completedCount = completed.size;
    setProgressPercentage(
      totalArticles > 0 ? Math.round((completedCount / totalArticles) * 100) : 0
    );
  };

  const handleToggleComplete = async () => {
    if (!selectedArticle || !user) return;

    const isCompleted = completedArticles.has(selectedArticle.id);
    const success = isCompleted
      ? await userGuideService.markArticleIncomplete(user.id, selectedArticle.id)
      : await userGuideService.markArticleCompleted(user.id, selectedArticle.id);

    if (success) {
      const newCompleted = new Set(completedArticles);
      if (isCompleted) {
        newCompleted.delete(selectedArticle.id);
        toast.success('Article marked as incomplete');
      } else {
        newCompleted.add(selectedArticle.id);
        toast.success('Article marked as complete!');
      }
      setCompletedArticles(newCompleted);

      const totalArticles = userGuideCategories.reduce(
        (sum, cat) => sum + cat.articles.length,
        0
      );
      setProgressPercentage(
        totalArticles > 0 ? Math.round((newCompleted.size / totalArticles) * 100) : 0
      );
    }
  };

  const handleToggleBookmark = async () => {
    if (!selectedArticle || !user) return;

    const isBookmarked = bookmarkedArticles.has(selectedArticle.id);
    const success = await userGuideService.toggleBookmark(
      user.id,
      selectedArticle.id,
      !isBookmarked
    );

    if (success) {
      const newBookmarked = new Set(bookmarkedArticles);
      if (isBookmarked) {
        newBookmarked.delete(selectedArticle.id);
        toast.success('Bookmark removed');
      } else {
        newBookmarked.add(selectedArticle.id);
        toast.success('Article bookmarked');
      }
      setBookmarkedArticles(newBookmarked);
    }
  };

  const getNextArticle = (): UserGuideArticle | null => {
    if (!selectedArticle) return null;

    for (let i = 0; i < userGuideCategories.length; i++) {
      const category = userGuideCategories[i];
      const currentIndex = category.articles.findIndex((a) => a.id === selectedArticle.id);

      if (currentIndex !== -1) {
        if (currentIndex < category.articles.length - 1) {
          return category.articles[currentIndex + 1];
        } else if (i < userGuideCategories.length - 1) {
          return userGuideCategories[i + 1].articles[0];
        }
      }
    }
    return null;
  };

  const getPreviousArticle = (): UserGuideArticle | null => {
    if (!selectedArticle) return null;

    for (let i = 0; i < userGuideCategories.length; i++) {
      const category = userGuideCategories[i];
      const currentIndex = category.articles.findIndex((a) => a.id === selectedArticle.id);

      if (currentIndex !== -1) {
        if (currentIndex > 0) {
          return category.articles[currentIndex - 1];
        } else if (i > 0) {
          const prevCategory = userGuideCategories[i - 1];
          return prevCategory.articles[prevCategory.articles.length - 1];
        }
      }
    }
    return null;
  };

  const filteredCategories = searchQuery
    ? userGuideCategories.map((category) => ({
        ...category,
        articles: category.articles.filter(
          (article) =>
            article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.content.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((category) => category.articles.length > 0)
    : userGuideCategories;

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  const isCompleted = selectedArticle ? completedArticles.has(selectedArticle.id) : false;
  const isBookmarked = selectedArticle ? bookmarkedArticles.has(selectedArticle.id) : false;
  const nextArticle = getNextArticle();
  const previousArticle = getPreviousArticle();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="flex items-center text-primary hover:text-primary/80"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 hover:bg-gray-100 rounded-lg md:hidden"
              >
                <Search className="w-5 h-5 text-gray-600" />
              </button>

              <div className="hidden md:flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="font-medium">{progressPercentage}% Complete</span>
              </div>

              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {showSearch && (
            <div className="mt-4 relative">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-80 transition-transform duration-300 ease-in-out lg:block`}
        >
          <UserGuideSidebar
            categories={filteredCategories}
            selectedArticle={selectedArticle}
            onArticleSelect={(article) => {
              setSelectedArticle(article);
              setSidebarOpen(false);
            }}
            completedArticles={completedArticles}
            bookmarkedArticles={bookmarkedArticles}
            className="h-full"
          />
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="flex-1 overflow-y-auto" ref={contentRef}>
          {selectedArticle ? (
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900 mb-3">
                        {selectedArticle.title}
                      </h1>
                      <p className="text-gray-600 mb-4">{selectedArticle.description}</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            difficultyColors[selectedArticle.difficulty]
                          }`}
                        >
                          {selectedArticle.difficulty.charAt(0).toUpperCase() +
                            selectedArticle.difficulty.slice(1)}
                        </span>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{selectedArticle.estimatedReadTime} min read</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleToggleComplete}
                      variant={isCompleted ? 'default' : 'outline'}
                      className="flex items-center gap-2"
                    >
                      {isCompleted ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Completed
                        </>
                      ) : (
                        <>
                          <Circle className="w-4 h-4" />
                          Mark Complete
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={handleToggleBookmark}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      {isBookmarked ? (
                        <>
                          <BookmarkCheck className="w-4 h-4" />
                          Bookmarked
                        </>
                      ) : (
                        <>
                          <Bookmark className="w-4 h-4" />
                          Bookmark
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="p-8 prose prose-lg max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children, ...props }) => (
                        <h1
                          {...props}
                          className="text-2xl font-bold text-gray-900 mt-8 mb-4 first:mt-0"
                        >
                          {children}
                        </h1>
                      ),
                      h2: ({ children, ...props }) => (
                        <h2 {...props} className="text-xl font-semibold text-gray-900 mt-8 mb-4">
                          {children}
                        </h2>
                      ),
                      h3: ({ children, ...props }) => (
                        <h3 {...props} className="text-lg font-semibold text-gray-900 mt-6 mb-3">
                          {children}
                        </h3>
                      ),
                      ul: ({ children, ...props }) => (
                        <ul {...props} className="space-y-2 my-4">
                          {children}
                        </ul>
                      ),
                      ol: ({ children, ...props }) => (
                        <ol {...props} className="space-y-2 my-4 list-decimal list-inside">
                          {children}
                        </ol>
                      ),
                      li: ({ children, ...props }) => (
                        <li {...props} className="flex items-start">
                          <span className="text-primary mr-2 mt-1">•</span>
                          <span>{children}</span>
                        </li>
                      ),
                      blockquote: ({ children, ...props }) => (
                        <blockquote
                          {...props}
                          className="border-l-4 border-primary bg-primary/5 p-4 my-6 rounded-r-lg"
                        >
                          {children}
                        </blockquote>
                      ),
                      code: ({ children, ...props }) => (
                        <code
                          {...props}
                          className="bg-gray-100 px-2 py-1 rounded text-sm font-mono"
                        >
                          {children}
                        </code>
                      ),
                    }}
                  >
                    {selectedArticle.content}
                  </ReactMarkdown>
                </div>

                <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex justify-between items-center">
                    {previousArticle ? (
                      <Button
                        variant="outline"
                        onClick={() => setSelectedArticle(previousArticle)}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                    ) : (
                      <div />
                    )}

                    {nextArticle ? (
                      <Button onClick={() => setSelectedArticle(nextArticle)}>
                        Next
                        <ChevronLeft className="w-4 h-4 ml-1 rotate-180" />
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <Award className="w-5 h-5" />
                        <span className="font-medium">You've completed this section!</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Select an article to get started
                </h2>
                <p className="text-gray-600">
                  Choose from the categories on the left to begin learning
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGuidePage;
